import type {
  MarketingInvoice,
  MarketingInvoiceDocumentType,
  MarketingInvoiceItemInput,
  MarketingInvoiceStatus,
  MarketingPayment,
  MarketingPaymentGroupInput,
  MarketingPaymentInstallmentInput,
  MarketingPaymentSplitMode,
} from '../../types/marketingNotas'
import type { MarketingRequest, MarketingRequestItem } from '../../types/marketingSolicitacao'
import type { PaymentMethod } from '../../types/estoque'
import {
  formatRequestNumber,
  formatRequesterWithDepartment,
  isStockDeductibleItemType,
  parseItemQuantity,
} from './solicitacaoUi'

export const MODULE_NAME = 'marketing_notas'
export const PARAMS_MODULE_NAME = 'marketing_parametros'
export const PERM_LOADING = null
export const PAGE_SIZE = 20

export const SELECT_CLASS =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white'

export const TEXTAREA_CLASS =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white'

export const ICON_BTN_CLASS =
  'rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-gray-700'

export const ICON_BTN_DANGER_CLASS =
  'rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20'

export function documentTypeLabel(type: MarketingInvoiceDocumentType | string): string {
  return type === 'nfse' ? 'NFS-e' : 'NF-e'
}

export function statusLabel(status: MarketingInvoiceStatus | string): string {
  return status === 'confirmed' ? 'Confirmada' : 'Rascunho'
}

export function documentTypeBadgeClass(type: MarketingInvoiceDocumentType | string): string {
  return type === 'nfse'
    ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
    : 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
}

export function statusBadgeClass(status: MarketingInvoiceStatus | string): string {
  return status === 'confirmed'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
}

export function formatCurrency(value: number | null | undefined): string {
  const n = Number(value ?? 0)
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function formatDateOnlyPtBr(value: string | null | undefined): string {
  if (!value) return '—'
  const [y, m, d] = value.slice(0, 10).split('-')
  if (!y || !m || !d) return value
  return `${d}/${m}/${y}`
}

export function calcItemLineTotal(
  quantity: number,
  unitValue: number,
  discountValue = 0
): number {
  const gross = Math.round(Number(quantity || 0) * Number(unitValue || 0) * 10000) / 10000
  const discount = Math.max(0, Number(discountValue || 0))
  return Math.round(Math.max(0, gross - discount) * 10000) / 10000
}

/** Derivado de total líquido + desconto ÷ quantidade (inverso de calcItemLineTotal). */
export function calcItemUnitValue(
  quantity: number,
  totalValue: number,
  discountValue = 0
): number {
  const qty = Number(quantity || 0)
  if (!(qty > 0)) return 0
  const total = Math.max(0, Number(totalValue || 0))
  const discount = Math.max(0, Number(discountValue || 0))
  return Math.round(((total + discount) / qty) * 10000) / 10000
}

export function invoiceItemsTotal(invoice: MarketingInvoice): number {
  return (invoice.items || []).reduce((acc, item) => acc + Number(item.total_value || 0), 0)
}

export function invoiceTotal(invoice: MarketingInvoice): number {
  const items = invoiceItemsTotal(invoice)
  const freight =
    invoice.document_type === 'nfe' ? Number(invoice.freight_value || 0) : 0
  const discount = Number(invoice.discount_value || 0)
  return Math.round((items + freight - discount) * 10000) / 10000
}

/** Extrai brinde/material impresso da solicitação para pré-preencher NF-e. */
export function itemsFromRequestStock(
  items: MarketingRequestItem[] | undefined
): MarketingInvoiceItemInput[] {
  const result: MarketingInvoiceItemInput[] = []
  for (const item of items || []) {
    if (!isStockDeductibleItemType(item.item_type)) continue
    const details = (item.details || {}) as Record<string, unknown>
    const stockItemId = String(details.item_id || '').trim()
    if (!stockItemId) continue
    const qty = parseItemQuantity(details.quantidade)
    if (!Number.isFinite(qty) || qty <= 0) continue
    const label = String(details.item || details.material || '').trim()
    result.push({
      stock_item_id: stockItemId,
      description: label || null,
      quantity: qty,
      unit_value: 0,
      discount_value: 0,
      total_value: 0,
    })
  }
  return result
}

export function emptyDraftItem(
  documentType: MarketingInvoiceDocumentType
): MarketingInvoiceItemInput {
  if (documentType === 'nfe') {
    return {
      stock_item_id: '',
      description: '',
      quantity: 1,
      unit_value: 0,
      discount_value: 0,
      total_value: 0,
    }
  }
  return {
    stock_item_id: null,
    description: '',
    quantity: 1,
    unit_value: 0,
    discount_value: 0,
    total_value: 0,
  }
}

/** Rótulo do select: #N Solicitante (Departamento): Título. */
export function formatRequestSelectLabel(
  request: Pick<MarketingRequest, 'request_number' | 'title' | 'requester'>
): string {
  const number = formatRequestNumber(request.request_number)
  const requester = formatRequesterWithDepartment(request.requester)
  const title = (request.title || '').trim() || 'Sem título'
  return `${number} ${requester}: ${title}`
}

export type PaymentDraftGroup = {
  key: string
  payment_method_id: string
  split_mode: MarketingPaymentSplitMode
  installments: MarketingPaymentInstallmentInput[]
}

function roundMoney(value: number): number {
  return Math.round(value * 10000) / 10000
}

export function addMonthsToDateOnly(dateStr: string, months: number): string {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number)
  const date = new Date(y, m - 1 + months, d)
  // Ajuste se o dia estourou o mês (ex.: 31 → feb)
  if (date.getDate() !== d) {
    date.setDate(0)
  }
  const yy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

export function splitAmountIntoInstallments(
  totalAmount: number,
  count: number,
  firstDueDate: string
): MarketingPaymentInstallmentInput[] {
  const n = Math.max(1, Math.floor(count))
  const total = roundMoney(Math.max(0, totalAmount))
  const base = roundMoney(Math.floor((total / n) * 100) / 100)
  const installments: MarketingPaymentInstallmentInput[] = []
  let allocated = 0
  for (let i = 0; i < n; i++) {
    const amount = i === n - 1 ? roundMoney(total - allocated) : base
    allocated = roundMoney(allocated + amount)
    installments.push({
      installment_number: i + 1,
      amount,
      due_date: addMonthsToDateOnly(firstDueDate, i),
    })
  }
  return installments
}

export function emptyPaymentGroup(
  remainingAmount: number,
  issueDate?: string,
  options?: { pendingAmount?: boolean }
): PaymentDraftGroup {
  const due = (issueDate || new Date().toISOString().slice(0, 10)).slice(0, 10)
  return {
    key: `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    payment_method_id: '',
    split_mode: 'single',
    installments: [
      {
        installment_number: 1,
        amount: options?.pendingAmount ? null : roundMoney(Math.max(0, remainingAmount)),
        due_date: due,
      },
    ],
  }
}

export function paymentGroupsTotal(groups: PaymentDraftGroup[]): number {
  return roundMoney(
    groups.reduce(
      (acc, group) =>
        acc +
        group.installments.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      0
    )
  )
}

export function paymentsFromInvoiceRows(
  payments: MarketingPayment[] | undefined,
  methods: PaymentMethod[]
): PaymentDraftGroup[] {
  if (!payments?.length) return []
  const map = new Map<number, PaymentDraftGroup>()
  const sorted = [...payments].sort((a, b) => {
    if (a.group_index !== b.group_index) return a.group_index - b.group_index
    return a.installment_number - b.installment_number
  })
  for (const row of sorted) {
    let group = map.get(row.group_index)
    if (!group) {
      const method = methods.find((m) => m.id === row.payment_method_id)
      group = {
        key: `g-${row.group_index}-${row.payment_method_id}`,
        payment_method_id: row.payment_method_id,
        split_mode:
          method?.settlement === 'avista'
            ? 'single'
            : sorted.filter((p) => p.group_index === row.group_index).length > 1
              ? 'manual'
              : 'single',
        installments: [],
      }
      map.set(row.group_index, group)
    }
    group.installments.push({
      installment_number: row.installment_number,
      amount: row.amount == null ? null : Number(row.amount),
      due_date: row.due_date.slice(0, 10),
    })
  }
  return Array.from(map.values())
}

export function toPaymentGroupInputs(groups: PaymentDraftGroup[]): MarketingPaymentGroupInput[] {
  return groups.map((group, index) => ({
    payment_method_id: group.payment_method_id,
    group_index: index,
    split_mode: group.split_mode,
    installments: group.installments.map((row, i) => {
      const raw = row.amount
      const amount =
        raw == null || raw === ('' as unknown)
          ? null
          : Number.isFinite(Number(raw)) && Number(raw) > 0
            ? Number(raw)
            : null
      return {
        installment_number: row.installment_number || i + 1,
        amount,
        due_date: row.due_date,
      }
    }),
  }))
}

/** Usuário cancelou o window.confirm de conclusão — não tratar como erro de UI. */
export class ConfirmCancelledError extends Error {
  constructor() {
    super('CONFIRM_CANCELLED')
    this.name = 'ConfirmCancelledError'
  }
}

export function isConfirmCancelled(err: unknown): boolean {
  return err instanceof ConfirmCancelledError
}

/** Resumo compacto de pagamentos para logs / detalhe. */
export function paymentsLogSummary(payments: MarketingPayment[] | undefined) {
  const rows = payments || []
  const groups = new Map<
    number,
    { payment_method_id: string; method_name: string | null; installments: number; amount: number }
  >()
  let paymentsTotal = 0
  for (const row of rows) {
    const amount = Number(row.amount || 0)
    paymentsTotal = roundMoney(paymentsTotal + amount)
    const current = groups.get(row.group_index) || {
      payment_method_id: row.payment_method_id,
      method_name: row.payment_method?.name || null,
      installments: 0,
      amount: 0,
    }
    current.installments += 1
    current.amount = roundMoney(current.amount + amount)
    groups.set(row.group_index, current)
  }
  return {
    payments_count: rows.length,
    payment_groups: Array.from(groups.entries()).map(([group_index, group]) => ({
      group_index,
      ...group,
    })),
    payments_total: paymentsTotal,
  }
}
